import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { ADD_WORDS } from "@/lib/placeWords";
import { Check, Loader2, Undo2 } from "lucide-react";

const chip = (on) => `text-left rounded-xl border-2 px-3 py-2 transition-colors min-h-[48px] ${on
  ? "border-[#E0781A] bg-orange-50" : "border-slate-200 bg-white hover:border-slate-400"}`;

// STEP 1 — WHAT IS IT? Your templates first, then only the things that can go here, each with its one line.
export function WhatChips({ templates, kinds, what, tplId, onPick }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="add-here-choices">
      {templates.map((t) => (
        <button key={t.id} type="button" data-testid={`add-here-tpl-${t.id}`} onClick={() => onPick(t.what, t.id)} className={chip(tplId === t.id)}>
          <div className="text-sm font-bold text-slate-900">{t.name}</div>
          <div className="text-[11px] text-slate-500">your template · {t.summary}</div>
        </button>
      ))}
      {kinds.map((k) => (
        <button key={k} type="button" data-testid={`add-here-kind-${k}`} onClick={() => onPick(k, null)} className={`${chip(!tplId && what === k)} max-w-[260px]`}>
          <div className="text-sm font-bold text-slate-900">{ADD_WORDS[k].word}</div>
          <div className="text-[11px] text-slate-500 leading-snug">{ADD_WORDS[k].line}</div>
        </button>
      ))}
    </div>
  );
}

// SLOT LABELS ON 4×3 OR 3×1 — his call after a test print on the Zebra; either works on every Make
export function SlotSizePick({ value, onChange }) {
  return (
    <div className="space-y-1" data-testid="add-here-slot-size">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Slot labels</div>
      <div className="flex gap-2">
        {[["4x3", "4×3 — same roll as the holder"], ["3x1", "3×1 — small, the roll changes once"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`add-here-slot-size-${v}`} onClick={() => onChange(v)}
            className={`flex-1 rounded-xl border-2 px-3 py-2 text-sm text-left transition-colors ${value === v ? "border-[#E0781A] bg-orange-50 font-bold" : "border-slate-200"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export function Stepper({ label, value, onChange, min = 1, max = 60, testid }) {
  const set = (v) => onChange(Math.max(min, Math.min(max, Number(v) || min)));
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-28 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <button type="button" data-testid={`${testid}-minus`} onClick={() => set(value - 1)} className="h-10 w-10 rounded-lg border border-slate-300 text-lg">−</button>
      <Input data-testid={testid} type="number" value={value} min={min} max={max} onChange={(e) => set(e.target.value)} className="h-10 w-20 text-center font-bold" />
      <button type="button" data-testid={`${testid}-plus`} onClick={() => set(value + 1)} className="h-10 w-10 rounded-lg border border-slate-300 text-lg">+</button>
    </label>
  );
}

// EVERY CODE BEFORE ANYTHING IS MADE — the first few, and all of them on a tap
export function PlanCodes({ plan, error }) {
  const [all, setAll] = useState(false);
  if (error) return <p data-testid="add-here-plan-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-[#B01E28]">{error}</p>;
  if (!plan) return null;
  const shown = all ? plan.codes : plan.codes.slice(0, 6);
  return (
    <div data-testid="add-here-plan" className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
        The app will make <span data-testid="add-here-plan-count">{plan.count}</span> code{plan.count === 1 ? "" : "s"}
      </div>
      <div className="flex flex-wrap gap-1 font-mono text-[12px]">
        {shown.map((c) => <span key={c} data-testid={`add-here-code-${c}`} className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5">{c}</span>)}
        {plan.codes.length > 6 && (
          <button type="button" data-testid="add-here-show-all" onClick={() => setAll((a) => !a)} className="text-[12px] text-sky-700 underline px-1">
            {all ? "show fewer" : `show all ${plan.codes.length}`}
          </button>
        )}
      </div>
      {plan.then_scan && (
        <p className="mt-2 text-[12px] text-slate-600">It is made off any place — your scan puts it on <b className="font-mono">{plan.onto}</b>.</p>
      )}
    </div>
  );
}

// THE LAST STEP FOR A HOLDER — his scan rule: it isn't on the place until it is scanned there
function ScanOnto({ code, onto, onMoved }) {
  const [done, setDone] = useState(null);
  const go = async (v) => {
    try {
      const { data } = await api.post("/containers/move", { container_scan: code, location_scan: v });
      if (!data.ok) { scanRefuse(data.message || "THAT DIDN'T MOVE"); setDone({ ok: false, message: data.message }); return; }
      scanAccept(`${code} → ${data.destination?.short_code}`);
      setDone({ ok: true, to: data.destination?.short_code });
      onMoved?.();
    } catch (e) { const m = formatApiErrorDetail(e.response?.data?.detail); scanRefuse(m); setDone({ ok: false, message: m }); }
  };
  if (done?.ok) return <div data-testid={`scan-onto-done-${code}`} className="text-sm text-emerald-700 font-semibold"><Check className="inline w-4 h-4 mr-1" />{code} is on {done.to} now.</div>;
  return (
    <div className="space-y-1" data-testid={`scan-onto-${code}`}>
      <div className="text-sm">Now scan <b className="font-mono">{code}</b> onto <b className="font-mono">{onto}</b> — scan {onto}'s label.</div>
      <ScanField testid={`scan-onto-field-${code}`} label={null} submitLabel="Put it there" placeholder={`Scan ${onto}…`} onScan={go} autoFocus={false} />
      {done && !done.ok && <p data-testid={`scan-onto-error-${code}`} className="text-[12px] text-[#B01E28]">{done.message}</p>}
    </div>
  );
}

export function MadeBox({ made, onUndo, undoBusy, undone, onPrint, onRackLabels, onMoved }) {
  const codes = made.made.map((m) => m.short_code);
  return (
    <div data-testid="add-here-made" className="space-y-3">
      <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-900">
        <div className="font-bold" data-testid="add-here-made-count">{undone ? "Put back." : `Made ${made.count}: ${codes[0]}${codes.length > 1 ? ` … ${codes[codes.length - 1]}` : ""}`}</div>
        {made.saved_template && <div className="text-[12px] mt-1" data-testid="add-here-saved-template">Saved as the template "{made.saved_template.name}" — next time it's one tap.</div>}
        {undone && <div className="text-[12px] mt-1">{undone}</div>}
      </div>
      {!undone && made.then_scan && made.holders.map((h) => <ScanOnto key={h} code={h} onto={made.onto} onMoved={onMoved} />)}
      {!undone && (
        <div className="flex flex-wrap gap-2">
          {made.what === "pallet_rack"
            ? <Button data-testid="add-here-rack-labels" onClick={onRackLabels} className="rounded-lg bg-[#1F1F1F] text-white">Print labels</Button>
            : <Button data-testid="add-here-print" onClick={onPrint} className="rounded-lg bg-[#1F1F1F] text-white">Print {made.count} label{made.count === 1 ? "" : "s"}</Button>}
          <Button data-testid="add-here-undo" variant="outline" disabled={undoBusy} onClick={onUndo} className="rounded-lg border-slate-300">
            {undoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}Undo
          </Button>
        </div>
      )}
    </div>
  );
}
